const settingsKey = 'dead-loop-settings-v1';
const defaultColors = {
  hat: '#ff4f68',
  shirt: '#45d2ff',
  pants: '#202b56',
  shoes: '#e9f5ff',
  projectile: '#53ffb5',
};
const $ = (id) => document.getElementById(id);

let lobby = null;

export function initLobby(options = {}) {
  const settings = loadSettings();
  lobby = {
    options,
    settings,
    root: $('lobby'),
    hud: $('hud'),
    weaponHud: $('weaponHud'),
    settingsModal: $('settingsModal'),
    startButton: $('startBtn'),
    backButton: $('backToLobbyBtn'),
    pauseButton: $('pauseBtn'),
    settingsButton: $('settingsBtn'),
    qualitySelect: $('qualitySelect'),
    volumeRange: $('volumeRange'),
    volumeText: $('volumeText'),
    shakeToggle: $('shakeToggle'),
    fullscreenButton: $('fullscreenBtn'),
    heroPicker: $('heroPicker'),
    roomCode: $('roomCode'),
    regenRoom: $('regenRoom'),
    randomColors: $('randomColors'),
    resetColors: $('resetColors'),
    colorInputs: Array.from(document.querySelectorAll('[data-color-key]')),
  };
  ensureAdvancedSettings();
  Object.assign(lobby, {
    fovRange: $('fovRange'),
    fovText: $('fovText'),
    hudScaleRange: $('hudScaleRange'),
    hudScaleText: $('hudScaleText'),
    shakeIntensityRange: $('shakeIntensityRange'),
    shakeIntensityText: $('shakeIntensityText'),
    flashReductionToggle: $('flashReductionToggle'),
    colorblindSelect: $('colorblindSelect'),
    heroPicker: $('heroPicker'),
  });

  applySettings(settings, options);
  bindLobbyEvents();
  return lobby;
}

export function showLobby() {
  if (!lobby) initLobby();
  lobby.root?.classList.remove('hidden');
  lobby.hud?.classList.add('hidden');
  lobby.weaponHud?.classList.add('hidden');
}

export function hideLobby() {
  if (!lobby) initLobby();
  lobby.root?.classList.add('hidden');
  lobby.hud?.classList.remove('hidden');
  lobby.weaponHud?.classList.remove('hidden');
}

export function openSettings() {
  if (!lobby) initLobby();
  lobby.settingsModal?.classList.remove('hidden');
}

export function closeModal(id) {
  $(id)?.classList.add('hidden');
}

export function loadSettings() {
  try {
    return {
      volume: 0.32,
      quality: 'low',
      shake: true,
      shakeIntensity: 1,
      fov: 65,
      hudScale: 1,
      flashReduction: false,
      colorblind: 'none',
      hero: 'sahur',
      room: createRoomCode(),
      colors: { ...defaultColors },
      ...(JSON.parse(localStorage.getItem(settingsKey) || '{}')),
    };
  } catch {
    return { volume: 0.32, quality: 'low', shake: true, shakeIntensity: 1, fov: 65, hudScale: 1, flashReduction: false, colorblind: 'none', hero: 'sahur', room: createRoomCode(), colors: { ...defaultColors } };
  }
}

export function saveSettings(settings = lobby?.settings) {
  if (!settings) return;
  localStorage.setItem(settingsKey, JSON.stringify(settings));
}

function bindLobbyEvents() {
  const { settings } = lobby;
  lobby.startButton?.addEventListener('click', () => {
    hideLobby();
    lobby.options.onStart?.();
  });
  lobby.backButton?.addEventListener('click', () => {
    showLobby();
    lobby.options.onLobby?.();
  });
  lobby.pauseButton?.addEventListener('click', () => {
    lobby.options.onPause?.();
  });
  lobby.settingsButton?.addEventListener('click', openSettings);
  document.querySelectorAll('[data-close-modal]').forEach((button) => {
    button.addEventListener('click', () => closeModal(button.dataset.closeModal));
  });
  window.addEventListener('keydown', (event) => {
    if (event.code === 'Escape') {
      closeModal('settingsModal');
      closeModal('leaderboardModal');
    }
  });
  lobby.qualitySelect?.addEventListener('change', () => {
    settings.quality = lobby.qualitySelect.value;
    saveSettings(settings);
    lobby.options.onQualityChange?.(settings.quality);
  });
  lobby.volumeRange?.addEventListener('input', () => {
    settings.volume = Number(lobby.volumeRange.value) / 100;
    saveSettings(settings);
    updateVolumeText();
    lobby.options.onVolumeChange?.(settings.volume);
  });
  lobby.shakeToggle?.addEventListener('change', () => {
    settings.shake = lobby.shakeToggle.value !== 'off';
    saveSettings(settings);
    lobby.options.onShakeChange?.(settings.shake);
  });
  lobby.shakeIntensityRange?.addEventListener('input', () => {
    settings.shakeIntensity = Number(lobby.shakeIntensityRange.value) / 100;
    saveSettings(settings);
    updateAdvancedSettingText();
    lobby.options.onShakeIntensityChange?.(settings.shakeIntensity);
  });
  lobby.fovRange?.addEventListener('input', () => {
    settings.fov = Number(lobby.fovRange.value);
    saveSettings(settings);
    updateAdvancedSettingText();
    lobby.options.onFovChange?.(settings.fov);
  });
  lobby.hudScaleRange?.addEventListener('input', () => {
    settings.hudScale = Number(lobby.hudScaleRange.value) / 100;
    saveSettings(settings);
    updateAdvancedSettingText();
    applyHudScale(settings.hudScale);
  });
  lobby.flashReductionToggle?.addEventListener('change', () => {
    settings.flashReduction = lobby.flashReductionToggle.checked;
    saveSettings(settings);
    lobby.options.onFlashReductionChange?.(settings.flashReduction);
  });
  lobby.colorblindSelect?.addEventListener('change', () => {
    settings.colorblind = lobby.colorblindSelect.value;
    saveSettings(settings);
    applyColorblind(settings.colorblind);
  });
  lobby.fullscreenButton?.addEventListener('click', () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      document.documentElement.requestFullscreen?.();
    }
  });
  lobby.regenRoom?.addEventListener('click', () => {
    settings.room = createRoomCode();
    saveSettings(settings);
    updateRoomCode();
  });
  lobby.randomColors?.addEventListener('click', () => {
    settings.colors = randomColorKit();
    saveSettings(settings);
    updateColorInputs();
    applyColorKit(settings.colors);
    lobby.options.onColorsChange?.(settings.colors);
  });
  lobby.resetColors?.addEventListener('click', () => {
    settings.colors = { ...defaultColors };
    saveSettings(settings);
    updateColorInputs();
    applyColorKit(settings.colors);
    lobby.options.onColorsChange?.(settings.colors);
  });
  lobby.colorInputs.forEach((input) => {
    input.addEventListener('input', () => {
      settings.colors = { ...defaultColors, ...settings.colors, [input.dataset.colorKey]: input.value };
      saveSettings(settings);
      applyColorKit(settings.colors);
      lobby.options.onColorsChange?.(settings.colors);
    });
  });
  lobby.heroPicker?.addEventListener('click', (event) => {
    const button = event.target.closest('[data-hero-id]');
    if (!button) return;
    settings.hero = button.dataset.heroId;
    saveSettings(settings);
    updateHeroPicker();
    lobby.options.onHeroChange?.(settings.hero);
  });
}

function applySettings(settings, options) {
  if (lobby.qualitySelect) lobby.qualitySelect.value = settings.quality;
  if (lobby.volumeRange) lobby.volumeRange.value = Math.round(settings.volume * 100);
  if (lobby.shakeToggle) lobby.shakeToggle.value = settings.shake ? 'on' : 'off';
  if (lobby.shakeIntensityRange) lobby.shakeIntensityRange.value = Math.round(settings.shakeIntensity * 100);
  if (lobby.fovRange) lobby.fovRange.value = settings.fov;
  if (lobby.hudScaleRange) lobby.hudScaleRange.value = Math.round(settings.hudScale * 100);
  if (lobby.flashReductionToggle) lobby.flashReductionToggle.checked = !!settings.flashReduction;
  if (lobby.colorblindSelect) lobby.colorblindSelect.value = settings.colorblind;
  settings.colors = { ...defaultColors, ...settings.colors };
  updateVolumeText();
  updateAdvancedSettingText();
  updateRoomCode();
  updateColorInputs();
  applyColorKit(settings.colors);
  applyHudScale(settings.hudScale);
  applyColorblind(settings.colorblind);
  updateHeroPicker();
  options.onQualityChange?.(settings.quality);
  options.onVolumeChange?.(settings.volume);
  options.onShakeChange?.(settings.shake);
  options.onShakeIntensityChange?.(settings.shakeIntensity);
  options.onFovChange?.(settings.fov);
  options.onFlashReductionChange?.(settings.flashReduction);
  options.onHeroChange?.(settings.hero);
  options.onColorsChange?.(settings.colors);
}

function updateVolumeText() {
  if (lobby?.volumeText) lobby.volumeText.textContent = `${Math.round(lobby.settings.volume * 100)}%`;
}

function updateAdvancedSettingText() {
  if (!lobby) return;
  if (lobby.fovText) lobby.fovText.textContent = `${lobby.settings.fov}`;
  if (lobby.hudScaleText) lobby.hudScaleText.textContent = `${Math.round(lobby.settings.hudScale * 100)}%`;
  if (lobby.shakeIntensityText) lobby.shakeIntensityText.textContent = `${Math.round(lobby.settings.shakeIntensity * 100)}%`;
}

function applyHudScale(scale = 1) {
  const hud = $('hud');
  if (!hud) return;
  hud.style.transform = `scale(${scale})`;
  hud.style.transformOrigin = 'top left';
}

function applyColorblind(mode = 'none') {
  const mount = $('canvasMount');
  if (!mount) return;
  const filters = {
    none: '',
    protanopia: 'hue-rotate(90deg) saturate(1.5)',
    deuteranopia: 'hue-rotate(45deg) saturate(1.35)',
    tritanopia: 'hue-rotate(180deg) saturate(1.4)',
  };
  mount.style.filter = filters[mode] || '';
}

function ensureAdvancedSettings() {
  const grid = document.querySelector('.settings-grid');
  if (!grid || $('fovRange')) return;
  const rows = [
    settingRow('fovRange', 'FOV', '<span id="fovText">65</span>', '<input id="fovRange" type="range" min="50" max="90" value="65" />'),
    settingRow('hudScaleRange', 'HUD scale', '<span id="hudScaleText">100%</span>', '<input id="hudScaleRange" type="range" min="80" max="120" value="100" />'),
    settingRow('shakeIntensityRange', 'Shake intensity', '<span id="shakeIntensityText">100%</span>', '<input id="shakeIntensityRange" type="range" min="0" max="100" value="100" />'),
    settingRow('flashReductionToggle', 'Flash reduction', '', '<input id="flashReductionToggle" type="checkbox" />'),
    settingRow('colorblindSelect', 'Colorblind mode', '', '<select id="colorblindSelect"><option value="none">None</option><option value="protanopia">Protanopia</option><option value="deuteranopia">Deuteranopia</option><option value="tritanopia">Tritanopia</option></select>'),
  ];
  const fullscreenRow = $('fullscreenBtn')?.closest('.setting-row');
  for (const row of rows) grid.insertBefore(row, fullscreenRow || null);
  ensureHeroPicker();
}

function ensureHeroPicker() {
  if ($('heroPicker')) return;
  const start = $('startBtn');
  if (!start?.parentElement) return;
  const picker = document.createElement('div');
  picker.id = 'heroPicker';
  picker.className = 'hero-picker';
  picker.innerHTML = [
    heroButton('sahur', 'Sahur', 'heavy'),
    heroButton('sprout', 'Sprout', 'nimble'),
    heroButton('warden', 'Patapim', 'tank'),
  ].join('');
  start.parentElement.insertBefore(picker, start);
}

function updateHeroPicker() {
  document.querySelectorAll('[data-hero-id]').forEach((button) => {
    button.classList.toggle('selected', button.dataset.heroId === lobby?.settings?.hero);
  });
}

function updateRoomCode() {
  if (lobby?.roomCode) lobby.roomCode.textContent = lobby.settings.room || 'ZMB-000';
}

function updateColorInputs() {
  if (!lobby) return;
  lobby.colorInputs.forEach((input) => {
    input.value = lobby.settings.colors?.[input.dataset.colorKey] || defaultColors[input.dataset.colorKey] || '#ffffff';
  });
}

function applyColorKit(colors = defaultColors) {
  const root = document.documentElement;
  Object.entries({ ...defaultColors, ...colors }).forEach(([key, value]) => {
    root.style.setProperty(`--${key}`, value);
  });
}

function randomColorKit() {
  const palettes = [
    ['#ff3156', '#53ffb5', '#202b56', '#e9f5ff', '#53ffb5'],
    ['#ffd166', '#6aa9ff', '#111827', '#f2f7ff', '#ffd166'],
    ['#f35b04', '#f7f7ff', '#232946', '#b8c1ec', '#ff8906'],
    ['#8cffda', '#ff5d8f', '#1b1b3a', '#f3f8ff', '#8cffda'],
  ];
  const pick = palettes[Math.floor(Math.random() * palettes.length)];
  return {
    hat: pick[0],
    shirt: pick[1],
    pants: pick[2],
    shoes: pick[3],
    projectile: pick[4],
  };
}

function createRoomCode() {
  return `ZMB-${Math.floor(100 + Math.random() * 900)}`;
}

function heroButton(id, label, role) {
  return `<button type="button" data-hero-id="${id}"><strong>${label}</strong><span>${role}</span></button>`;
}

function settingRow(id, label, valueMarkup, controlMarkup) {
  const row = document.createElement('div');
  row.className = 'setting-row';
  row.innerHTML = `<label for="${id}">${label} ${valueMarkup}</label>${controlMarkup}`;
  return row;
}
